/**
 * Toolbar — workspace toggle, panel visibility buttons, panel close buttons
 */
import { uiStore } from '../stores/uiStore';
import type { PanelVisibility, WorkspaceMode } from '@graphicsim/shared-types';
import { applyLayout } from '../engine/layoutEngine';

/** Track previous layout and library state to detect changes */
let prevLayout = '';
let prevLibraryVisible: boolean | null = null;

export function initToolbar(): void {
  // Setup/Live workspace toggle
  document.querySelectorAll<HTMLButtonElement>('.toggle-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.workspace as WorkspaceMode;
      if (mode) {
        uiStore.getState().setWorkspace(mode);
      }
    });
  });

  // Icon toolbar buttons → panel toggles
  document.querySelectorAll<HTMLButtonElement>('.icon-tb-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const panel = btn.dataset.panel as keyof PanelVisibility | undefined;
      if (panel) {
        uiStore.getState().togglePanel(panel);
      }
    });
  });

  // Panel close buttons (X in panel headers)
  document.querySelectorAll<HTMLButtonElement>('.panel-close-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const panel = btn.dataset.close as keyof PanelVisibility | undefined;
      if (panel) {
        uiStore.getState().setPanel(panel, false);
      }
    });
  });

  // Subscribe to UI store for visual updates
  uiStore.subscribe(updateToolbarVisuals);

  // Initial state
  updateToolbarVisuals();
}

function updateToolbarVisuals(): void {
  const { workspace, panels, statusText, toolbarVisible, workspaceFocus, activeLayout } = uiStore.getState();

  // Apply layout rearrangement when layout preset changes
  // Also re-apply when library visibility changes (setup layout has conditional header row)
  const libraryChanged = prevLibraryVisible !== null && prevLibraryVisible !== panels.library;
  if ((activeLayout !== prevLayout && activeLayout !== 'custom') || libraryChanged) {
    prevLayout = activeLayout;
    prevLibraryVisible = panels.library;
    if (activeLayout !== 'custom') {
      applyLayout(activeLayout, panels);
    }
  }
  prevLibraryVisible = panels.library;

  // Toggle icon toolbar visibility
  const iconToolbar = document.getElementById('icon-toolbar');
  if (iconToolbar) {
    iconToolbar.style.display = toolbarVisible ? '' : 'none';
  }

  // Update workspace toggle
  document.querySelectorAll<HTMLButtonElement>('.toggle-btn').forEach((btn) => {
    btn.classList.toggle('active', btn.dataset.workspace === workspace);
  });

  // Update toolbar button active states
  document.querySelectorAll<HTMLButtonElement>('.icon-tb-btn').forEach((btn) => {
    const panel = btn.dataset.panel as keyof PanelVisibility | undefined;
    if (panel && panel in panels) {
      btn.classList.toggle('tb-active', panels[panel]);
    }
  });

  // Show/hide panels via data-panel-id attribute
  // Use 'flex' (not '') so panels keep their flex column layout for header + body
  document.querySelectorAll<HTMLElement>('[data-panel-id]').forEach((el) => {
    const panelId = el.dataset.panelId as keyof PanelVisibility;
    if (panelId in panels) {
      if (panels[panelId]) {
        el.style.display = 'flex';
        el.style.flexDirection = 'column';
      } else {
        el.style.display = 'none';
      }
    }
  });

  // Update workspace focus outline (locks panel movement)
  document.querySelectorAll<HTMLElement>('[data-panel-id]').forEach((el) => {
    el.classList.remove('ws-focused');
  });
  if (workspaceFocus !== 'none') {
    // workspaceFocus is 'project' or 'shots' which matches data-panel-id values
    document.querySelector<HTMLElement>(`[data-panel-id="${workspaceFocus}"]`)?.classList.add('ws-focused');
  }

  // Update status bar
  const statusbar = document.getElementById('statusbar');
  if (statusbar) {
    statusbar.textContent = statusText;
  }
}
